import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    Box,
    Button,
    CardContent, Paper, Table,
    TableBody,
    TableContainer,
    Typography
} from "@mui/material";
import PageLayout from "../components/layout/PageLayout";
import {useDispatch, useSelector} from "react-redux";
import {
    fetchEarlierTransactions,
    fetchLaterTransactions,
    selectTransactionsDays,
    selectTransactionsEarliestDate,
    selectTransactionsLatestDate
} from "../store/transactionSlice";
import EditTransactionForm from "../components/form/EditTransactionForm";
import {AddCircle} from "@mui/icons-material";
import EditChangeBudgetForm from "../components/form/EditChangeBudgetForm";
import EditSetBalanceForm from "../components/form/EditSetBalanceForm";
import Transaction from "../components/entry/Transaction";
import {useTranslation} from "react-i18next";
import MainScreenWalletsCard, {MAIN_SCREEN_WALLETS_ANCHOR_ID} from "../components/layout/MainScreenWalletsCard";
import {fetchWalletsAndGroups, selectMainScreenWallets} from "../store/walletSlice";
import {fetchCurrencies} from "../store/currencySlice";

// Start loading next page when this far from the edge (≈ 2–3 transactions before end)
const LOAD_AHEAD_PX = 500;

const todayISO = () => new Date().toISOString().slice(0, 10);

const TransactionsPage = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const transactionsDays = useSelector(state => selectTransactionsDays(state));
    const earliestDate = useSelector(state => selectTransactionsEarliestDate(state));
    const latestDate = useSelector(state => selectTransactionsLatestDate(state));
    const mainScreenWallets = useSelector(selectMainScreenWallets);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const scrollRef = useRef(null);
    const loadingOlderRef = useRef(false);
    const loadingNewerRef = useRef(false);
    const initialScrollDoneRef = useRef(false);

    useEffect(() => {
        dispatch(fetchEarlierTransactions());
        dispatch(fetchWalletsAndGroups());
        dispatch(fetchCurrencies());
    }, []);

    // transactionsDays come sorted DESC by date. Place the wallets card right
    // above the first day that's not in the future (today, or the most recent
    // past day if there is no "today" in the list). If every loaded day is in
    // the future, drop the card to the very bottom so it still appears below
    // them.
    const walletsCardInsertIndex = useMemo(() => {
        if (!transactionsDays || transactionsDays.length === 0) return -1;
        const today = todayISO();
        return transactionsDays.findIndex((day) => day.date <= today);
    }, [transactionsDays]);

    // Initial focus point: scroll to the wallets card (which sits right above
    // today / the most recent past day). If the user has no eligible wallets
    // (card never renders), fall back to today's day-block. We re-run on both
    // transactions and wallets state so we don't lock the scroll to "today"
    // when wallets finish loading after transactions.
    useEffect(() => {
        if (!transactionsDays?.length || !scrollRef.current || initialScrollDoneRef.current) return;
        const walletsEl = document.getElementById(MAIN_SCREEN_WALLETS_ANCHOR_ID);
        if (walletsEl) {
            initialScrollDoneRef.current = true;
            walletsEl.scrollIntoView({behavior: "auto", block: "start"});
            return;
        }
        // No wallets card will ever render → focus on today as before. If
        // wallets are still being fetched (mainScreenWallets is empty but we
        // can't yet tell whether that's "no data" or "still loading") we
        // simply wait — once wallets resolve we'll re-enter this effect.
        if (mainScreenWallets && mainScreenWallets.length === 0) {
            const today = todayISO();
            const dayEl = document.getElementById("day-" + today);
            if (dayEl) {
                initialScrollDoneRef.current = true;
                dayEl.scrollIntoView({behavior: "auto", block: "start"});
            }
        }
    }, [transactionsDays, mainScreenWallets]);

    const onScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;

        const { scrollTop, scrollHeight, clientHeight } = el;
        const atTop = scrollTop <= LOAD_AHEAD_PX;
        const atBottom = scrollHeight - clientHeight - scrollTop <= LOAD_AHEAD_PX;

        if (atBottom && earliestDate != null && !loadingOlderRef.current) {
            loadingOlderRef.current = true;
            dispatch(fetchEarlierTransactions()).finally(() => {
                loadingOlderRef.current = false;
            });
        }
        if (atTop && latestDate != null && !loadingNewerRef.current) {
            loadingNewerRef.current = true;
            dispatch(fetchLaterTransactions()).finally(() => {
                loadingNewerRef.current = false;
            });
        }
    }, [dispatch, earliestDate, latestDate]);

    return (
        <PageLayout
            title={t("pages.transactions.title")}
            cardStyle={{ display: "flex", flexDirection: "column" }}
        >
            <CardContent
                ref={scrollRef}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    position: "relative"
                }}
                onScroll={onScroll}
            >
                {transactionsDays && transactionsDays.map((day, i) =>
                    <React.Fragment key={day.date}>
                        {i === walletsCardInsertIndex && <MainScreenWalletsCard/>}
                        <Box sx={{marginBottom: 4}} id={"day-" + day.date}>
                            <Typography variant="h6" color="primary" sx={{marginLeft: 4}}>{day.date}</Typography>
                            <TableContainer component={Paper}>
                                <Table sx={{minWidth: 650}} aria-label="simple table">
                                    <TableBody >
                                        {day.transactions.map((transaction, index) => {
                                            const key = transaction.id ?? (transaction.templateDateKey != null && transaction.templateId != null
                                                ? `${transaction.templateDateKey}-${transaction.templateId}`
                                                : transaction.templateDateKey != null && transaction.budget != null
                                                    ? `${transaction.templateDateKey}-${transaction.budget}`
                                                    : `day-${day.date}-${index}`);
                                            return (
                                            <Transaction
                                                key={key}
                                                transaction={transaction}
                                                onClick={setSelectedTransaction}
                                            />
                                        ); })
                                        }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </React.Fragment>)
                }
                {transactionsDays && transactionsDays.length > 0 && walletsCardInsertIndex === -1 && <MainScreenWalletsCard/>}
            </CardContent>
            <Button
                sx={{
                    position: "absolute",
                    bottom: 24,
                    left: "50%",
                    transform: "translateX(-50%)",
                    fontSize: "4rem",
                    zIndex: 2
                }}
                onClick={() => {
                    setSelectedTransaction({description: "", type: "EXPENSE", date: new Date()})
                }}
            >
                <AddCircle fontSize="inherit"/>
            </Button>
            {selectedTransaction &&
                ( ['INCOME' , 'EXPENSE', 'TRANSFER'].includes(selectedTransaction.type) ?
                        <EditTransactionForm transaction={selectedTransaction} onCancel={(e) => setSelectedTransaction(null)}></EditTransactionForm> :
                        'BUDGET' === selectedTransaction.type ? <EditChangeBudgetForm changeBudgetTransaction={selectedTransaction} onCancel={(e) => setSelectedTransaction(null)}/> :
                            <EditSetBalanceForm setBalance={selectedTransaction} onCancel={(e) => setSelectedTransaction(null)}/>
                )
            }
        </PageLayout>


        
    );

    {/*<div>*/
    }
    {/*    <NavigationMenu></NavigationMenu>*/
    }
    {/*    <Card style={{marginLeft: 330, marginTop: 10, marginRight: 10, marginBottom: 10}}>*/
    }
    {/*        <EficsHeader>Transactions</EficsHeader>*/
    }
    {/*        <CardContent>*/
    }
    {/*            <Box sx={{marginBottom: 4}}>*/
    }
    {/*                <Typography variant="h6" color="primary" sx={{marginLeft: 4}}>05/10/2024</Typography>*/
    }
    {/*                <TableContainer component={Paper}>*/
    }
    {/*                    <Table sx={{minWidth: 650}} aria-label="simple table">*/
    }
    {/*                        <TableBody>*/
    }
    {/*                            {rows.map((row) => (*/
    }
    {/*                                <TableRow*/
    }
    {/*                                    key={row.name}*/
    }
    {/*                                    sx={{'&:last-child td, &:last-child th': {border: 0}}}*/
    }
    {/*                                >*/
    }
    {/*                                    <TableCell component="th" scope="row">*/
    }
    {/*                                        {row.name}*/
    }
    {/*                                    </TableCell>*/
    }
    {/*                                    <TableCell align="right">{row.calories}</TableCell>*/
    }
    {/*                                    <TableCell align="right">{row.fat}</TableCell>*/
    }
    {/*                                    <TableCell align="right">{row.carbs}</TableCell>*/
    }
    {/*                                    <TableCell align="right">{row.protein}</TableCell>*/
    }
    {/*                                </TableRow>*/
    }
    {/*                            ))}*/
    }
    {/*                        </TableBody>*/
    }
    {/*                    </Table>*/
    }
    {/*                </TableContainer>*/
    }
    {/*            </Box>*/
    }
    {/*            <Box>*/
    }
    {/*                <Typography variant="h6" color="primary" sx={{marginLeft: 4}}>05/10/2024</Typography>*/
    }
    {/*                <TableContainer component={Paper}>*/
    }
    {/*                    <Table sx={{minWidth: 650}} aria-label="simple table">*/
    }
    {/*                        <TableBody>*/
    }
    {/*                            {rows.map((row) => (*/
    }
    {/*                                <TableRow*/
    }
    {/*                                    key={row.name}*/
    }
    {/*                                    sx={{'&:last-child td, &:last-child th': {border: 0}}}*/
    }
    {/*                                >*/
    }
    {/*                                    <TableCell component="th" scope="row">*/
    }
    {/*                                        {row.name}*/
    }
    {/*                                    </TableCell>*/
    }
    {/*                                    <TableCell align="right">{row.calories}</TableCell>*/
    }
    {/*                                    <TableCell align="right">{row.fat}</TableCell>*/
    }
    {/*                                    <TableCell align="right">{row.carbs}</TableCell>*/
    }
    {/*                                    <TableCell align="right">{row.protein}</TableCell>*/
    }
    {/*                                </TableRow>*/
    }
    {/*                            ))}*/
    }
    {/*                        </TableBody>*/
    }
    {/*                    </Table>*/
    }
    {/*                </TableContainer>*/
    }
    {/*            </Box>*/
    }
    {/*        </CardContent>*/
    }
    {/*    </Card>*/
    }
    {/*</div>*/
    }
}

export default TransactionsPage